import { useState } from 'react';
import { Box, Icon, Link, Title } from '@nimbus-ds/components';
import { ArrowLeftIcon, ArrowRightIcon } from '@nimbus-ds/icons';
import { SelectionRequest } from '@tiendanube/common';
import { Stack, SubmitButton, useResponsive } from 'commons/components';
import Logo from 'domains/Auth/components/Logo';
import useTranslationPreOnboarding from 'domains/PreOnboarding/useTranslationPreOnboarding';
import { TaskCheckboxCard, TaskInputCard, TaskRadioCard } from '..';
import './QuestionnaireForm.scss';

interface QuestionnaireFormProps {
  onSubmit: () => void;
  onReturn?: () => void;
  values: SelectionRequest[];
  setValues: (v: SelectionRequest[]) => void;
  currentGroup: string;
  isCheckbox: boolean;
  questionnaireOptions: {
    id: number;
    code: string;
    label: string;
    description?: string;
  }[];
  onSkipGroup?: (groupKey: string, skipId: number) => void;
  isLastStep?: boolean;
}

export function QuestionnaireForm({
  onSubmit,
  onReturn,
  values,
  setValues,
  currentGroup,
  isCheckbox,
  questionnaireOptions,
  onSkipGroup,
  isLastStep,
}: Readonly<QuestionnaireFormProps>) {
  const t = useTranslationPreOnboarding();
  const { isMobile } = useResponsive();
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  const handleChangeTask = (id: number, checked: boolean) => {
    if (checked) {
      let updated: SelectionRequest[];

      if (isCheckbox) {
        const isDontKnow = dontKnow?.id === id;

        if (isDontKnow) {
          updated = [{ domain_mapping_id: id }];
        } else {
          updated = [...values, { domain_mapping_id: id }];
          updated = updated.filter(
            (v) =>
              v.domain_mapping_id !== skipped?.id &&
              v.domain_mapping_id !== dontKnow?.id,
          );
        }
      } else {
        updated = [{ domain_mapping_id: id }];
      }

      setValues(updated);
    } else if (isCheckbox) {
      setValues(values.filter((v) => v.domain_mapping_id !== id));
    }
  };

  const handleSkip = () => {
    if (skipped && onSkipGroup) {
      onSkipGroup(currentGroup, skipped.id);
    }
  };

  const customOnSubmit = () => {
    setIsLoadingPage(true);
    onSubmit();
  };

  const handleFreeTextChange = (id: number) => (newFreeText: string) => {
    const onlyWhitespace = newFreeText.trim() === '';

    const exists = values.find((item) => item.domain_mapping_id === id);
    let newValues = [...values];

    if (exists) {
      if (!onlyWhitespace) {
        newValues = newValues.map((item) =>
          item.domain_mapping_id === id
            ? { ...item, free_text: newFreeText }
            : item,
        );
      } else {
        newValues = newValues.filter((item) => item.domain_mapping_id !== id);
      }
    } else if (!onlyWhitespace) {
      newValues.push({ domain_mapping_id: id, free_text: newFreeText });
    }
    if (dontKnow && id !== dontKnow.id) {
      newValues = newValues.filter((v) => v.domain_mapping_id !== dontKnow?.id);
    }
    setValues(newValues);
  };

  const options = questionnaireOptions.filter(
    (option) => !option.code.includes('other') && !option.code.includes('skip'),
  );
  const skipped = questionnaireOptions.find((option) =>
    option.code.includes('skip'),
  );
  const other = questionnaireOptions.find((option) =>
    option.code.includes('other'),
  );
  const dontKnow = questionnaireOptions.find((option) =>
    option.code.includes('dont_know'),
  );

  const freeText = other
    ? values.find((item) => item.domain_mapping_id === other.id)?.free_text ??
      ''
    : '';

  return (
    <Box
      flex="1"
      padding={isMobile ? '8' : '16'}
      display="flex"
      flexDirection="column"
      height="100%"
      data-style="stratus--questionnaire-insti-font"
    >
      <Box>
        <Logo />
        <Box marginBottom="6">
          <Title as="h2" color="primary-textHigh">
            {t(`questionnaire.${currentGroup}.title`)}
          </Title>
        </Box>

        <Box
          display="grid"
          gap="4"
          gridTemplateColumns={options.length > 5 ? '1fr 1fr' : '1fr'}
        >
          {isCheckbox
            ? options.map((opt) => (
                <TaskCheckboxCard
                  key={opt.id}
                  task={opt}
                  checked={values.some(
                    (selection) => selection.domain_mapping_id === opt.id,
                  )}
                  onChange={handleChangeTask}
                />
              ))
            : options.map((opt) => (
                <TaskRadioCard
                  key={opt.id}
                  task={opt}
                  selectedId={values[0]?.domain_mapping_id ?? null}
                  onChange={(id) => handleChangeTask(id, true)}
                  name={t(`questionnaire.${currentGroup}.title`)}
                />
              ))}
        </Box>
        {other && (
          <Box paddingTop="4">
            <TaskInputCard
              freeText={freeText}
              onChange={handleFreeTextChange(other.id)}
              label={t(`questionnaire.${currentGroup}.other`)}
            />
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop="auto"
        paddingTop="4"
      >
        <Stack gap="4">
          {onReturn && (
            <SubmitButton
              onClick={onReturn}
              disabled={isLoadingPage}
              data-style="stratus--questionnaire-insti-button-secondary"
              data-variant={isMobile ? 'icon-only' : undefined}
            >
              {isMobile ? (
                <Icon color="currentColor" source={<ArrowLeftIcon />} />
              ) : (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap="2"
                >
                  <Icon color="currentColor" source={<ArrowLeftIcon />} />
                  {t(`questionnaire.return`)}
                </Box>
              )}
            </SubmitButton>
          )}
          <SubmitButton
            onClick={isLastStep ? customOnSubmit : onSubmit}
            isLoading={isLoadingPage}
            appearance="primary"
            data-style="stratus--questionnaire-insti-button"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap="2"
            >
              {isLastStep
                ? t(`questionnaire.submit`)
                : t(`questionnaire.continue`)}
              <Icon color="currentColor" source={<ArrowRightIcon />} />
            </Box>
          </SubmitButton>
        </Stack>
        {skipped && (
          <Link
            as="button"
            textDecoration="none"
            onClick={handleSkip}
            appearance="primary"
            data-style="stratus--questionnaire-insti-button-skip"
          >
            {t(`questionnaire.skip`)}
          </Link>
        )}
      </Box>
    </Box>
  );
}
